import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./individual.css";
import "bootstrap/dist/css/bootstrap.min.css";

// Componente Individual
const Individual = ({ filters }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [longestLivedTicket, setLongestLivedTicket] = useState({});
  const [topRatedAgent, setTopRatedAgent] = useState({});
  const [worstRatedAgent, setWorstRatedAgent] = useState({});
  const [redTicketCount, setRedTicketCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [showingRedTickets, setShowingRedTickets] = useState(false);
  const navigate = useNavigate();

  // Usando useRef para controlar el primer renderizado
  const firstRender = useRef(true);

  const getAgentRating = useCallback((tickets, compareFn) => {
    const agentRatings = tickets.reduce((acc, ticket) => {
      const { assigned_id, rating } = ticket;
      if (assigned_id && rating != null) {
        if (!acc[assigned_id]) {
          acc[assigned_id] = { totalRating: 0, count: 0 };
        }
        acc[assigned_id].totalRating += rating;
        acc[assigned_id].count += 1;
      }
      return acc;
    }, {});

    return Object.entries(agentRatings).map(([agentId, { totalRating, count }]) => ({
      agentId,
      averageRating: totalRating / count
    })).sort(compareFn)[0] || {};
  }, []);

  const getLongestLivedTicket = useCallback((tickets) => tickets.reduce((max, ticket) => {
    if (ticket.solved_at && ticket.created_at) {
      const duration = (ticket.solved_at - ticket.created_at) / 1000; // Asegúrate de que los timestamps están en milisegundos
      return duration > (max.duration || 0) ? { ...ticket, duration } : max;
    }
    return max;
  }, { duration: 0 }), []);

  const getTopRatedAgent = useCallback((tickets) => getAgentRating(tickets, (a, b) => b.averageRating - a.averageRating), [getAgentRating]);

  const getWorstRatedAgent = useCallback((tickets) => getAgentRating(tickets, (a, b) => a.averageRating - b.averageRating), [getAgentRating]);

  const fetchData = useCallback(async () => {
    if (!filters.endDate && !filters.group) return; // Si no hay fecha fin, no hacer la GET porque no se cargó nada para buscar.

    setIsLoading(true);
    setNoResults(false);
    const { priority, startDate, endDate, group, email } = filters;

    const params = new URLSearchParams({
      desde: startDate,
      hasta: endDate,
      priority,
      grupo: group,
      email
    }).toString();

    const backendUrl = process.env.REACT_APP_backend;
    const url = `${backendUrl}/extracto?${params}`;

    const backendKey = process.env.REACT_APP_backend_key;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: backendKey }
      });
      const fetchedData = response.data;
      setData(fetchedData);

      if (fetchedData.length === 0) {
        setNoResults(true);
      } else {
        const redTickets = fetchedData.filter(
          (ticket) => ticket.creator_id === ticket.assigned_id
        );
        setRedTicketCount(redTickets.length);
        setFilteredData(showingRedTickets ? redTickets : fetchedData);

        setLongestLivedTicket(getLongestLivedTicket(fetchedData));
        setTopRatedAgent(getTopRatedAgent(fetchedData));
        setWorstRatedAgent(getWorstRatedAgent(fetchedData));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setNoResults(true); // Muestra un mensaje de error en la UI
    } finally {
      setIsLoading(false);
    }
  }, [filters, showingRedTickets, getLongestLivedTicket, getTopRatedAgent, getWorstRatedAgent]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    fetchData();
  }, [filters, showingRedTickets, fetchData]);

  const showRedTickets = () => {
    setFilteredData(showingRedTickets ? data : data.filter(ticket => ticket.creator_id === ticket.assigned_id));
    setShowingRedTickets(!showingRedTickets);
  };
  return (
    <div>
      <div className="Qcontainer">
          <div className="retorno-button" onClick={() => navigate("/")}>
            Volver a tickets abiertos 😅
          </div>
      </div>

      <div className="info-cubes">
        <Card title="Ticket más Longevo" color="bg-primary" value={longestLivedTicket.title || "N/A"}>
          <p>ID: {longestLivedTicket.id || "N/A"}<br />
          Duración: {longestLivedTicket.duration ? (longestLivedTicket.duration / (60 * 60 * 24)).toFixed(2) + " días" : "N/A"}</p>
        </Card>
        <Card title="Agente más valorado" color="bg-secondary" value={topRatedAgent.agentId || "N/A"}>
          <p>Rating Promedio: {topRatedAgent.averageRating ? topRatedAgent.averageRating.toFixed(2) : "N/A"}</p>
        </Card>
        <Card title="Agente menos valorado" color="bg-success" value={worstRatedAgent.agentId || "N/A"}>
          <p>Rating Promedio: {worstRatedAgent.averageRating ? worstRatedAgent.averageRating.toFixed(2) : "N/A"}</p>
        </Card>
        <Card title="Asignados a su creador" color="bg-danger" onClick={showRedTickets} value={redTicketCount} />
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : noResults ? (
        <div className="no-results-container">
          <p>Sin resultados</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>#ID</th>
                <th>Prioridad</th>
                <th>Título</th>
                <th>Fecha de Creación</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((ticket) => (
                <tr key={ticket.id} className={ticket.creator_id === ticket.assigned_id ? "highlight-row" : ""}>
                  <td>{ticket.id}</td>
                  <td>{ticket.priority_id}</td>
                  <td>{ticket.title}</td>
                  <td>{new Date(ticket.created_at * 1000).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Componente Card simplificado para mostrar la información
const Card = ({ title, color, value, onClick, children }) => (
  <div className={`card text-white ${color} mb-3`} style={{ maxWidth: "18rem" }} onClick={onClick}>
    <div className="card-header">{title}</div>
    <div className="card-body">
      <h5 className="card-title">{value}</h5>
      {children}
    </div>
  </div>
);

export default Individual;
